import { useState, useEffect, useContext } from "react";
import { Award, Trophy, Star, Lock, ChevronRight, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

// Category colors
const CATEGORY_COLORS = {
  wealth: { bg: "bg-yellow-100", border: "border-yellow-400", text: "text-yellow-700" },
  tournament: { bg: "bg-purple-100", border: "border-purple-400", text: "text-purple-700" },
  games: { bg: "bg-green-100", border: "border-green-400", text: "text-green-700" },
  social: { bg: "bg-blue-100", border: "border-blue-400", text: "text-blue-700" },
  special: { bg: "bg-pink-100", border: "border-pink-400", text: "text-pink-700" }
};

// Badge Card Component
const BadgeCard = ({ badge, earned = false, progress = null, onClick }) => {
  const colors = CATEGORY_COLORS[badge.category] || CATEGORY_COLORS.special;
  
  return (
    <div
      onClick={onClick}
      className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all hover:scale-105 ${
        earned 
          ? `${colors.bg} ${colors.border}` 
          : 'bg-gray-100 border-gray-300 opacity-60'
      }`}
    >
      {/* Badge Icon */}
      <div className="text-center mb-2">
        <span className={`text-3xl ${earned ? '' : 'grayscale'}`}>{badge.icon}</span>
      </div>
      
      {/* Badge Name */}
      <div className={`text-center font-bold text-sm ${earned ? colors.text : 'text-gray-500'}`}>
        {badge.name}
      </div>
      
      {/* Progress Bar (if not earned) */}
      {!earned && progress !== null && (
        <div className="mt-2">
          <div className="h-1.5 bg-gray-300 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-center text-[10px] text-gray-500 mt-0.5">{progress}%</div>
        </div>
      )}
      
      {/* Lock Icon */}
      {!earned && (
        <div className="absolute top-1 right-1">
          <Lock className="w-3 h-3 text-gray-400" />
        </div>
      )}
      
      {/* Earned checkmark */}
      {earned && (
        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
          <Star className="w-2.5 h-2.5 text-white fill-white" />
        </div>
      )}
    </div>
  );
};

// Badge Detail Modal
const BadgeDetailModal = ({ badge, earned, earnedAt, onClose }) => {
  if (!badge) return null;
  
  const colors = CATEGORY_COLORS[badge.category] || CATEGORY_COLORS.special;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="text-center">
          {/* Icon */}
          <div className={`w-20 h-20 rounded-full ${colors.bg} flex items-center justify-center mx-auto mb-4`}>
            <span className="text-5xl">{badge.icon}</span>
          </div>
          
          {/* Name */}
          <h3 className={`text-xl font-bold ${colors.text}`}>{badge.name}</h3>
          
          {/* Category */}
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text} capitalize mt-1`}>
            {badge.category}
          </span>
          
          {/* Description */}
          <p className="text-gray-600 mt-3">{badge.description}</p>
          
          {/* Earned info */}
          {earned && earnedAt && (
            <div className="mt-4 bg-green-50 rounded-lg p-3">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Award className="w-5 h-5" />
                <span className="font-medium">Badge Earned!</span>
              </div>
              <div className="text-xs text-green-600 mt-1">
                {new Date(earnedAt).toLocaleDateString()}
              </div>
            </div>
          )}
          
          {!earned && (
            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Lock className="w-5 h-5" />
                <span>Not Yet Earned</span>
              </div>
            </div>
          )}
        </div>
        
        <button
          onClick={onClose}
          className="w-full mt-4 py-2 bg-gray-200 rounded-lg font-medium hover:bg-gray-300"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// New Badge Notification
const NewBadgeNotification = ({ badges, onClose }) => {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-gradient-to-b from-purple-900 to-indigo-900 rounded-xl p-6 w-full max-w-sm shadow-2xl text-center">
        <Sparkles className="w-12 h-12 text-yellow-400 mx-auto mb-4 animate-pulse" />
        <h2 className="text-2xl font-bold text-white mb-2">
          {badges.length === 1 ? 'New Badge Earned!' : `${badges.length} New Badges!`}
        </h2>
        
        <div className="flex justify-center gap-4 my-6">
          {badges.map(badge => (
            <div key={badge.id} className="text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                <span className="text-4xl">{badge.icon}</span>
              </div>
              <div className="text-white font-bold text-sm">{badge.name}</div>
            </div>
          ))}
        </div>
        
        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-400"
        >
          Awesome!
        </button>
      </div>
    </div>
  );
};

// Main Badges Panel
const BadgesPanel = ({ username }) => {
  const { token, user } = useContext(AuthContext);
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [newBadges, setNewBadges] = useState([]);
  const [activeTab, setActiveTab] = useState("earned"); // earned, all, progress

  const targetUser = username || user?.username;

  const fetchData = async () => {
    if (!token || !targetUser) return;
    setLoading(true);
    try {
      const [badgesRes, allBadgesRes, progressRes] = await Promise.all([
        axios.get(`${API}/badges/user/${targetUser}`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/badges/all`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/badges/progress/${targetUser}`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      setBadges(badgesRes.data.badges || []);
      setAllBadges(allBadgesRes.data.badges || []);
      setProgress(progressRes.data.progress || []);
    } catch (error) {
      console.error("Failed to fetch badges:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkNewBadges = async () => {
    if (!token || targetUser !== user?.username) return;
    try {
      const response = await axios.post(`${API}/badges/check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.new_badges?.length > 0) {
        setNewBadges(response.data.new_badges);
        fetchData(); // Refresh badges list
      }
    } catch (error) {
      console.error("Failed to check badges:", error);
    }
  };

  useEffect(() => {
    fetchData();
    // Check for new badges on mount
    if (targetUser === user?.username) {
      checkNewBadges();
    }
  }, [token, targetUser]);

  const earnedBadgeIds = badges.map(b => b.id);
  
  const categories = ["wealth", "tournament", "games", "social", "special"];

  return (
    <div className="retro-window max-h-80 flex flex-col" data-testid="badges-panel">
      <div className="retro-title-bar flex items-center justify-between px-2">
        <span className="flex items-center gap-1">
          <Award className="w-4 h-4" />
          <span className="text-xs font-bold">BADGES</span>
          <span className="text-[10px] bg-white/20 px-1 rounded">{badges.length}/{allBadges.length}</span>
        </span>
        <button onClick={fetchData} disabled={loading} className="text-[10px] hover:bg-white/20 px-1 rounded">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="bg-[#C0C0C0] flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-400">
          {[
            { id: "earned", label: `Earned (${badges.length})` },
            { id: "progress", label: "Progress" },
            { id: "all", label: "All" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-1 text-[10px] font-bold ${
                activeTab === tab.id 
                  ? 'bg-white border-t-2 border-purple-500' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2 retro-scrollbar bg-white">
          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Loading...</div>
          ) : activeTab === "earned" ? (
            badges.length === 0 ? (
              <div className="text-center py-8">
                <Award className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-gray-500 text-sm">No badges earned yet</p>
                <p className="text-gray-400 text-xs">Keep playing to earn badges!</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {badges.map(badge => (
                  <BadgeCard
                    key={badge.id}
                    badge={badge}
                    earned={true}
                    onClick={() => setSelectedBadge({ badge, earned: true, earnedAt: badge.earned_at })}
                  />
                ))}
              </div>
            )
          ) : activeTab === "progress" ? (
            progress.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-gray-500 text-sm">All badges earned!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {progress.map(item => (
                  <div
                    key={item.badge.id}
                    className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={() => setSelectedBadge({ badge: item.badge, earned: false })}
                  >
                    <span className="text-2xl grayscale opacity-60">{item.badge.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{item.badge.name}</div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{item.current}/{item.target}</div>
                      <div className="text-xs font-bold text-purple-600">{item.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-3">
              {categories.map(category => {
                const categoryBadges = allBadges.filter(b => b.category === category);
                if (categoryBadges.length === 0) return null;
                
                const colors = CATEGORY_COLORS[category];
                return (
                  <div key={category}>
                    <div className={`text-xs font-bold capitalize mb-1 ${colors.text}`}>
                      {category}
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {categoryBadges.map(badge => {
                        const earned = earnedBadgeIds.includes(badge.id);
                        const progressItem = progress.find(p => p.badge.id === badge.id);
                        return (
                          <BadgeCard
                            key={badge.id}
                            badge={badge}
                            earned={earned}
                            progress={progressItem?.percentage}
                            onClick={() => setSelectedBadge({ 
                              badge, 
                              earned, 
                              earnedAt: badges.find(b => b.id === badge.id)?.earned_at 
                            })}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Check Badges Button (only for own profile) */}
        {targetUser === user?.username && (
          <div className="p-1 bg-purple-100 border-t border-purple-300">
            <button
              onClick={checkNewBadges}
              className="w-full text-center text-xs text-purple-700 font-medium hover:underline flex items-center justify-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              Check for New Badges
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <BadgeDetailModal
          badge={selectedBadge.badge}
          earned={selectedBadge.earned}
          earnedAt={selectedBadge.earnedAt}
          onClose={() => setSelectedBadge(null)}
        />
      )}

      {/* New Badge Notification */}
      <NewBadgeNotification
        badges={newBadges}
        onClose={() => setNewBadges([])}
      />
    </div>
  );
};

export default BadgesPanel;
